import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import {
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import HomeIcon from '@mui/icons-material/Home'
import AnalyticsDashboardPage from './Dashboard'
import { LoadingSpinner } from '../../components/atoms'
import { getMonthStart, getCurrentDate } from '../../services/date'
import { useFetch } from '../../hooks'
import styles from './CampaignDashboard.module.scss'
import { apiEndpoints } from '../../consts'

function useQuery () {
  return new URLSearchParams(useLocation().search)
}

const CampaignDashboardPage = ({ isAuthenticated, userId, company }) => {
  const navigate = useNavigate()
  const { campaignName } = useParams()
  const query = useQuery()
  const { t } = useTranslation()
  const startString = query.get('start')
  const endString = query.get('end')

  const [startDate] = useState(startString ? new Date(startString) : getMonthStart())
  const [endDate] = useState(endString ? new Date(endString) : getCurrentDate())

  const encodedName = campaignName ? encodeURIComponent(campaignName) : ''
  const start = startDate.toISOString()
  const end = endDate.toISOString()

  const { data: campaignData, loading } = useFetch(
    `${apiEndpoints.callsByCampaign(encodedName)}?start=${start}&end=${end}`,
    {
      skip: !campaignName,
      dependencies: [campaignName, startDate, endDate],
      errorMessage: 'Failed to fetch campaign data'
    }
  )

  const handleBackClick = () => {
    navigate('/campaigns')
  }

  if (!isAuthenticated) {
    return <div>{t('errors.noPermission')}</div>
  }

  if (loading && !campaignData) {
    return <LoadingSpinner />
  }

  return (
    <div className={styles.root}>
      <Box className={styles.header}>
        <Paper className={styles.breadcrumbContainer} elevation={0}>
          <Tooltip title={t('campaigns.title')}>
            <IconButton
              className={styles.backButton}
              onClick={handleBackClick}
              aria-label={t('common.back')}
              size='small'
            >
              <ArrowBackIcon fontSize='small' />
            </IconButton>
          </Tooltip>

          <Breadcrumbs
            className={styles.breadcrumbs}
            separator={
              <NavigateNextIcon fontSize='small' className={styles.breadcrumbSeparator} />
            }
            aria-label='breadcrumb'
          >
            <Link
              color='inherit'
              href='/'
              onClick={(e) => {
                e.preventDefault()
                navigate('/')
              }}
              className={styles.breadcrumbLink}
            >
              <HomeIcon fontSize='small' className={styles.breadcrumbIcon} />
              <span className={styles.breadcrumbText}>{t('navigation.home')}</span>
            </Link>
            <Link
              color='inherit'
              href='/campaigns'
              onClick={(e) => {
                e.preventDefault()
                navigate('/campaigns')
              }}
              className={styles.breadcrumbLink}
            >
              <span className={styles.breadcrumbText}>{t('campaigns.title')}</span>
            </Link>
            <Typography className={styles.currentPage}>
              {decodeURIComponent(campaignName)}
            </Typography>
          </Breadcrumbs>
        </Paper>
      </Box>

      {/* Reuse the Dashboard component with campaign filtering */}
      <AnalyticsDashboardPage
        isAuthenticated={isAuthenticated}
        userId={userId}
        company={company}
        startDate={startDate}
        endDate={endDate}
        campaignFilter={campaignName}
      />
    </div>
  )
}

export default CampaignDashboardPage
